import React, { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import dayjs from 'op-utils/dayjs';

import { ROPatientContext } from 'op-pages/RO/PatientSummary/context';
import PhotoWidget from 'shared-components/components/UIFormComponents/PhotoWidget';

import { DetailedCardProps } from './types';
import { GET_ALERTS } from './PatientCardQueries';
import { useTheme, Typography, Stack, Box, Grid, useMediaQuery, Fade, Tooltip } from '@mui/material';
import { UX_FEATURE_OPTION_QUERY } from 'op-graphql/queries/Feature';
import ROPatientDetailCards from './ROPatientDetailCards';
import { formatPatientName } from 'op-utils/PatientHelper';
import PatientDetailsChip from './PatientDetailsChip';

const StyledTooltip = ({ children, title }: { children: React.ReactElement; title: string }) => {
  const theme = useTheme();
  return (
    <Tooltip
      title={title}
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: { sx: { bgcolor: theme.palette.grey[700] } },
        arrow: { sx: { color: theme.palette.grey[700] } },
      }}>
      {children}
    </Tooltip>
  );
};

const ROPatientCard = (props: DetailedCardProps): JSX.Element => {
  const { attributes, moreDetailsClickHandler, showMoreDetails } = props;

  const { data: featureOption } = useQuery(UX_FEATURE_OPTION_QUERY, {
    variables: { featureOptionName: 'scribe' },
  });
  const scribeEnabled = useMemo(() => featureOption?.uxFeatureOption?.active, [featureOption]);

  const { allergy, carepathPatientDiagnosis } = attributes;
  const { setPatientAlerts, patientDetails } = useContext(ROPatientContext);
  const address = attributes.address ? attributes.address.ukFormattedAddress : '-';
  const theme = useTheme();
  const isMediumScreen = useMediaQuery('(max-width: 1920px)');
  const isSmallScreen = useMediaQuery('(max-width: 1440px)');

  const allReferrers = patientDetails?.allReferrers || [];
  const getReferrer = (type: string) => allReferrers.filter((referrer: any) => referrer.relationshipType === type);
  const responsiblePhysician = getReferrer('oncologist')[0];
  const referringDoctor = getReferrer('referringSurgeon')[0];
  const responsiblePhysicianName = responsiblePhysician
    ? `${responsiblePhysician.practitioner.firstName} ${responsiblePhysician.practitioner.lastName}`
    : '-';
  const referringDoctorName = referringDoctor
    ? `${referringDoctor.practitioner.firstName} ${referringDoctor.practitioner.lastName}`
    : '-';
  const NoKnownAllergiesProps = { bubbleColor: theme.palette.grey[600], bubbleText: 'N' };
  const NoAllergiesProps = { bubbleColor: theme.palette.warning.dark, bubbleText: '' };

  const filteredAllergies = allergy?.filter((allergyInfo: any) => allergyInfo.status === 'Active');
  const isNoKnownAllergy = filteredAllergies?.some(
    (allergy: any): boolean =>
      allergy.name && ['no known drug allergies', 'no known allergies'].includes(allergy.name.toLowerCase()),
  );

  const getAllergyProps = (allergyList: any): any => {
    if (allergyList && allergyList.length === 0) return NoAllergiesProps;
    return isNoKnownAllergy
      ? NoKnownAllergiesProps
      : {
          bubbleColor: theme.palette.error.main,
          bubbleText: allergyList?.length,
        };
  };
  const allergyCardProps = getAllergyProps(filteredAllergies);

  const { data: alertsData } = useQuery(GET_ALERTS, {
    variables: { patientId: attributes.patientId },
    onCompleted: (data) => setPatientAlerts(data?.alerts),
  });
  const alerts = alertsData?.alerts;

  const diagnosis = carepathPatientDiagnosis.filter(
    (diagnosis: any) => diagnosis.isMostRecent && diagnosis.emrDiagnosisKey !== null,
  );
  const filteredDiagnosis = diagnosis
    .filter((diagnosis: any) => diagnosis.isMostRecent && diagnosis.emrDiagnosisKey)
    .sort((a: any, b: any) => dayjs(b.diagnosisDate).diff(dayjs(a.diagnosisDate)));
  return (
    <Grid container sx={{ padding: '16px 0', width: '100%' }}>
      <Grid item xs={6}>
        <Stack direction="row" sx={{ display: 'flex', width: '100%' }}>
          <Stack direction="row" sx={{ flex: 1 }}>
            <Box sx={{ marginRight: '8px', marginLeft: '13px', display: 'flex' }}>
              <PhotoWidget patientId={attributes.patientId} enableOverlay={true} deferFetch={true} />
            </Box>
            <Stack sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Typography data-testid="patient-name-block" variant="subtitle1">
                  {formatPatientName(
                    patientDetails?.firstName,
                    patientDetails?.middleName,
                    patientDetails?.preferredName,
                    patientDetails?.lastName,
                    patientDetails?.fullName,
                  )}
                </Typography>
                <PatientDetailsChip showMoreDetails={showMoreDetails} onClick={moreDetailsClickHandler} />
              </Box>
              <Typography variant="caption" sx={{ marginTop: '4px' }}>
                EMR: <strong>{attributes.emrInstance}</strong> | Patient ID: <strong>{attributes.ida}</strong> | Sex:{' '}
                <strong>{attributes.gender}</strong>
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  display: 'block',
                  width: '100%',
                }}>
                DOB: <strong>{attributes.fullAge}</strong> | Address: <strong>{address}</strong>
              </Typography>
              {isMediumScreen && !isSmallScreen && (
                <Typography
                  variant="caption"
                  sx={{
                    whiteSpace: 'normal',
                    wordWrap: 'break-word',
                    overflowWrap: 'break-word',
                    display: 'block',
                    width: '100%',
                  }}>
                  <StyledTooltip title="Responsible Physician">
                    <span>
                      Responsible: <strong>{responsiblePhysicianName}</strong>
                    </span>
                  </StyledTooltip>
                  {' | '}
                  <StyledTooltip title="Referring Physician">
                    <span>
                      Referring: <strong>{referringDoctorName}</strong>
                    </span>
                  </StyledTooltip>
                  {' | '}
                  <StyledTooltip title="Patient's Mobile Number">
                    <span>
                      Mobile: <strong>{attributes.mobile}</strong>
                    </span>
                  </StyledTooltip>
                  {' | '}
                  <StyledTooltip title="Patient's Landline Number">
                    <span>
                      Landline: <strong>{attributes.landline}</strong>
                    </span>
                  </StyledTooltip>
                </Typography>
              )}
              {isSmallScreen && (
                <Typography variant="caption">
                  <StyledTooltip title="Patient's Mobile Number">
                    <span>
                      Mobile: <strong>{attributes.mobile}</strong>
                    </span>
                  </StyledTooltip>
                  {' | '}
                  <StyledTooltip title="Patient's Landline Number">
                    <span>
                      Landline: <strong>{attributes.landline}</strong>
                    </span>
                  </StyledTooltip>
                </Typography>
              )}
            </Stack>
          </Stack>
          {!isMediumScreen && (
            <Stack sx={{ padding: '4px 36px 0px 16px' }}>
              <Typography variant="caption">
                Responsible: <strong>{responsiblePhysicianName}</strong>
              </Typography>
              <Typography variant="caption">
                Referring: <strong>{referringDoctorName}</strong>
              </Typography>
              <Typography variant="caption" sx={{ marginTop: '8px' }}>
                Mobile: <strong>{attributes.mobile}</strong>
              </Typography>
              <Typography variant="caption">
                Landline: <strong>{attributes.landline}</strong>
              </Typography>
            </Stack>
          )}
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Fade in={!showMoreDetails} timeout={400}>
          <Stack
            direction="row"
            sx={{ justifyContent: 'space-between', alignItems: 'center', padding: '0 16px 0 8px' }}>
            <ROPatientDetailCards
              filteredDiagnosis={filteredDiagnosis}
              filteredAllergies={filteredAllergies}
              allergyCardProps={allergyCardProps}
              alerts={alerts}
              scribeEnabled={scribeEnabled}
            />
          </Stack>
        </Fade>
      </Grid>
    </Grid>
  );
};

export default ROPatientCard;
